/* global Ecwid */
import { useState, useEffect, useCallback, useContext, useRef } from 'react'
import { useSelector, useDispatch, shallowEqual } from 'react-redux'
import { createUseStyles } from 'react-jss'
import { externalPreload } from '@/utils/dom'
import { Context as GeneralContext } from '@/context'
import { Context as LoadingContext } from '@/context/loading'
import LocomotiveScroll from '@/components/LocomotiveScroll'
import BtnLink from '@/components/BtnLink'
import SliderRange from '@/components/SliderRange'
import Fieldset from '@/components/Fieldset'
import Footer from '@/components/Footer'
import ProductItem from '@/components/ProductItem'
import Meta from '@/components/Meta'
import useUpdateEffect from '@/hooks/useUpdateEffect'
import useHandleAllMediaWithCb from '@/hooks/useHandleAllMediaWithCb'
import useMediaQuery from '@/hooks/useMediaQuery'
import actions from '@/actions'
import constants from '@/constants'
import classNames from 'classnames'
import style from './style'

const useStyles = createUseStyles(style)

const LIMIT = 30

const Shop = ({
  stub,
}) => {
  const { headerHeight, getImageUrl, isModalCartVisible } = useContext(GeneralContext)
  const { setReady } = useContext(LoadingContext)
  const classes = useStyles({ headerHeight, isModalCartVisible })
  const [visibleProducts, setVisibleProducts] = useState([])
  const [load, setLoad] = useState(false)
  const [filtersMobileOpen, setFiltersMobileOpen] = useState(false)
  const [allFilters, setAllFilters] = useState([])
  const [offset, setOffset] = useState(0)
  const [products, setProducts] = useState([])

  const queryParams = new URLSearchParams(window.location.search)
  const [priceFrom, setPriceFrom] = useState(queryParams.get('priceFrom'))
  const [priceTo, setPriceTo] = useState(queryParams.get('priceTo'))
  const [$filters, setFilterRef] = useState()
  const [queryString, setQueryString] = useState('')
  const $root = useRef()
  const $hero = useRef()
  const { MatchQuery: Md, NotMatchQuery: NotMd } = useMediaQuery('md')

  /*------------------------------
  Hooks to handle media loading & updating the scrollbar
  ------------------------------*/
  useHandleAllMediaWithCb({
    init: load,
    ref: $root.current?.ref,
  })

  /*------------------------------
  Redux Connect
  ------------------------------*/
  const { current, page, categories, ecwidFilters, mediaQueryKey, strings, total } = useSelector((state) => ({
    current: state.page.current,
    page: state.page.pages[stub] || {},
    total: state.shop.total || 0,
    categories: state.shop.categories || [],
    // storeProducts: state.shop.products || [],
    ecwidFilters: state.shop.filters || {},
    strings: state.language.string,
    mediaQueryKey: state.mediaQuery.key,
  }), shallowEqual)

  /*------------------------------
  Redux Actions
  ------------------------------*/
  const dispatch = useDispatch()
  const getPage = useCallback((index) => dispatch(actions.page.getDataPage(index)), [dispatch])
  const setCurrentPage = useCallback((index) => dispatch(actions.page.setCurrent(index)), [dispatch])
  const getFilters = useCallback(() => dispatch(actions.shop.getEcwidFilters()), [dispatch])
  const getFilteredProducts = useCallback((qs, limit) => dispatch(actions.shop.getFilteredProducts(qs, limit)), [dispatch])
  const setStoreProducts = useCallback((items) => dispatch(actions.shop.setStoreProducts(items)), [dispatch])

  /*------------------------------
  Complete Preload
  ------------------------------*/
  const completePreload = () => { // eslint-disable-line
    externalPreload(setLoad)
    setReady(true)
  }

  /*------------------------------
  Preload Main Images: preloading only thre three first image & imageHover
  ------------------------------*/
  const preloadMedia = () => completePreload()

  /*------------------------------
  Initialize (API Calls)
  ------------------------------*/
  const initialize = () => {
    if (current !== stub) setCurrentPage(stub, 'pages')
    getPage(stub)
    getFilters()
  }

  /*------------------------------
  Set Product by filtering all products by current slug category
  ------------------------------*/
  useEffect(() => {
    if (products.length > 0 && categories.length > 0) {
      const mappedProducts = products.map((item) => ({
        ...item,
        title: item.name,
        link: `${constants.urls.SITE}${window.pathSite}shop/${item.id}`,
        designer: categories?.find((cat) => cat.id === item.categoryIds[0])?.name,
        image: item.originalImageUrl,
        price: item.defaultDisplayedPriceFormatted,
        discountPrice: item.compareToPriceFormatted,
        isInStock: item.inStock,
        quantity: item.quantity,
        attributes: item.attributes,
      }))
      setVisibleProducts((oldProducts) => (offset > 0 ? [...oldProducts, ...mappedProducts] : mappedProducts))
    } else {
      setVisibleProducts([])
    }
  }, [products, categories])

  /*------------------------------
  Did Mount
  ------------------------------*/
  useEffect(() => {
    if (!load) initialize()
  }, [])

  /*------------------------------
  If there are products in category complete the preload
  ------------------------------*/
  useEffect(() => {
    if (products.length > 0 && Object.keys(page).length > 0) preloadMedia()
  }, [products, page])

  /*------------------------------
  Render Helmet
  ------------------------------*/
  const renderHelmet = () => {
    return load && (
      <Meta
        title={page.yoast_meta.find((y) => y.property?.includes('title')).content || page.title.rendered}
        meta={page.yoast_meta}
        schema={page.yoast_json_ld}
      />
    )
  }

  /*------------------------------
  Set All Filters
  ------------------------------*/
  useEffect(() => {
    if (Object.keys(ecwidFilters).length > 0) {
      setAllFilters(Object.keys(ecwidFilters).map((filter) => ({ name: filter, ...ecwidFilters[filter] })))
    }
  }, [ecwidFilters])

  /*------------------------------
  Render Hero
  ------------------------------*/
  const renderHero = () => {
    const imgSize = mediaQueryKey > 2 ? 'full' : 'large'
    const src = getImageUrl(page.banner_single_src, imgSize, stub, 'pages')
    return (
      <div className={classes.heroContainer} ref={$hero}>
        <img src={src} alt="" />
        <div className={classes.heroContent}>
          <h1>{page.banner_single_title}</h1>
          <div>
            <span>{page.banner_single_text}</span>
            {page.banner_single_link && (
            <BtnLink
              label={page.banner_single_label || 'Scopri'}
              link={page.banner_single_link}
              theme="light"
            />
            )}
          </div>

        </div>
      </div>
    )
  }

  /*------------------------------
  Add to cart
  ------------------------------*/
  const handleAddToCart = (id, quantity) => {
    if (Ecwid) Ecwid.Cart.addProduct({ id, quantity })
  }

  /*------------------------------
  Setto I prezzi minimi dai filtri
  ------------------------------*/
  useEffect(() => {
    if (ecwidFilters.price) {
      if (priceFrom === null) setPriceFrom(ecwidFilters.price.minValue)
      if (priceTo === null) setPriceTo(ecwidFilters.price.maxValue)
    }
  }, [ecwidFilters, priceFrom, priceTo])

  /*------------------------------
  Chiamata API
  ------------------------------*/
  useEffect(() => {
    if (!priceFrom || !priceTo) return
    if (Object.keys(ecwidFilters).length > 0) {
      getFilteredProducts({
        qs: queryString,
        limit: LIMIT,
      })
        .then((items) => {
          setProducts(items)
        })
      if (queryString !== '') {
        const url = window.location.href.split('?')[0]
        window.history.pushState('', '', `${url}?${queryString}`)
      }
    }
  }, [ecwidFilters, queryString, priceFrom, priceTo])

  /*------------------------------
  Abilito le checkbox dopo la prima chiamata
  E setto i price
  ------------------------------*/
  useEffect(() => {
    if ($filters) {
      const inputs = document.body.querySelectorAll('.form-filter [type="checkbox"], .form-filter [type="radio"]')
      Array.from(inputs).forEach((input) => {
        input.checked = queryParams.getAll(input.name)[0]?.split(',')?.includes(input.value.toString())
      })
    }
  }, [$filters])

  /*------------------------------
  Handle Filters
  ------------------------------*/
  const handleFilters = () => {
    let filter = {
      priceFrom,
      priceTo,
    }

    // Filter categories
    const fieldsets = document.querySelectorAll('.form-filter')

    fieldsets.forEach((fieldset) => {
      const name = fieldset.getAttribute('name')
      const output = [...fieldset.querySelectorAll('input:checked')].map((input) => input.value)
      if (output.length > 0) {
        filter = {
          ...filter,
          [name]: output,
        }
      }
    })

    const searchParams = new URLSearchParams(filter).toString()
    setQueryString(searchParams?.replace('?', ''))
    setOffset(0)
  }

  /*------------------------------
  HandleFilters al cambio di Prezzo
  ------------------------------*/
  useUpdateEffect(() => {
    if ($filters) handleFilters()
  }, [priceFrom, priceTo, $filters])

  useEffect(() => {
    if (queryString !== '') {
      const params = new URLSearchParams(queryString)
      params.set('offset', offset)
      setQueryString(params)
    }
  }, [offset])

  /*------------------------------
  Handle Read More
  ------------------------------*/
  const handleReadMore = () => {
    setOffset(visibleProducts.length)
  }

  /*------------------------------
  Set Products inside Store
  ------------------------------*/
  useEffect(() => {
    if (visibleProducts.length > 0) setStoreProducts(visibleProducts)
  }, [visibleProducts])

  /*------------------------------
  Render Filters
  ------------------------------*/
  const renderFilters = () => {
    return (
      <div className={classes.filter}>
        <form
          id="filters"
          ref={(r) => setFilterRef(r)}
        >
          {
            allFilters.map((filter) => {
              if (filter.name === 'price') {
                return (
                  <Fieldset
                    name="price"
                    title={strings.prezzo}
                    key={filter.name}
                  >
                    <SliderRange
                      minRange={filter.minValue}
                      maxRange={filter.maxValue}
                      min={priceFrom}
                      max={priceTo}
                      label="€"
                      reload={filtersMobileOpen}
                      onChange={({ min, max }) => {
                        setPriceFrom(min)
                        setPriceTo(max)
                      }}
                    />
                  </Fieldset>
                )
              }

              if (filter.name === 'inventory') return null

              if (filter.name === 'categories') {
                return filter.values?.length > 0 && (
                  <Fieldset
                    key={filter.name}
                    name={filter.name}
                    title={strings[filter.name]}
                    className="form-filter"
                  >
                    {
                    filter.values.map((cat, ind) => {
                      return (
                        <div className={classes.checkbox} key={ind.toString()}>
                          <label htmlFor={`${filter.name}-${ind}`}>
                            <input
                              name={filter.name}
                              id={`${filter.name}-${ind}`}
                              value={cat.id}
                              type="checkbox"
                              onChange={handleFilters}
                            />
                            <span>{cat.title}</span>
                          </label>
                        </div>
                      )
                    })
                  }
                  </Fieldset>
                )
              }

              return filter.values?.length > 0 && (
                <Fieldset
                  key={filter.name}
                  name={filter.name}
                  title={strings[filter.title?.toLowerCase()]}
                  className="form-filter"
                >
                  {
                    filter.values.map((cat, ind) => {
                      return (
                        <div className={classes.checkbox} key={ind.toString()}>
                          <label htmlFor={`${filter.name}-${ind}`}>
                            <input
                              name={filter.name}
                              id={`${filter.name}-${ind}`}
                              value={cat.title}
                              type="checkbox"
                              onChange={handleFilters}
                            />
                            <span>{cat.title}</span>
                          </label>
                        </div>
                      )
                    })
                  }
                </Fieldset>
              )
            })
          }

        </form>
      </div>
    )
  }

  /*------------------------------
  Render Filters Mobile
  ------------------------------*/
  const renderFilterMobile = () => {
    return (
      <div className={classNames({
        [classes.filtersMobile]: true,
        [classes.filtersMobileOpen]: filtersMobileOpen,
      })}
      >
        <div className={classes.filtersMobileContent}>
          <div className={classes.wrap}>
            <h3>{strings.filter_label_mobile}</h3>
            {renderFilters()}
          </div>

        </div>
        <div className={classes.filtersMobileFooter}>
          <div className={classes.wrap}>
            <button
              className={classes.btnApplyFilter}
              onClick={() => setFiltersMobileOpen(false)}
            >
              {strings.apply_filter_label}
            </button>
          </div>
        </div>
      </div>
    )
  }

  /*------------------------------
  Render Products
  ------------------------------*/
  const renderProducts = () => {
    return (
      <div className={classes.products} id="products">
        <NotMd>
          <div className={classes.sidebar}>
            {renderFilters()}
          </div>
        </NotMd>
        {visibleProducts.length > 0
          ? (
            <ul className={classes.productView}>
              {visibleProducts.map((product) => (
                <ProductItem
                  key={product.id}
                  item={product}
                  shop
                  onAddCartClick={() => handleAddToCart(product.id, 1)}
                />
              ))}
            </ul>
          ) : strings.noproducts}
      </div>
    )
  }

  /*------------------------------
  Render Footer
  ------------------------------*/
  const renderFooter = () => {
    return load && <Footer />
  }

  /*------------------------------
  Render Content
  ------------------------------*/
  const renderContent = () => {
    return load && (
      <>
        {renderHero()}
        <div className={classes.wrap}>
          <div className={classes.page}>
            <Md>
              <button
                className={classes.btnOpenFiltersMobile}
                onClick={() => setFiltersMobileOpen(true)}
              >
                <svg>
                  <use xlinkHref="#ico-filters" />
                </svg>
                {strings.open_filter_label}
              </button>
            </Md>
            {renderProducts()}
            {total > visibleProducts.length ? (
              <div className={classes.readmore}>
                <BtnLink
                  label={strings.load_more_products}
                  onClick={handleReadMore}
                  theme="dark"
                />
              </div>
            ) : null}
          </div>
        </div>
      </>
    )
  }

  return (
    <LocomotiveScroll
      init={load}
      className={`page pageShop ${classes.root}`}
      ref={$root}
    >
      {renderHelmet()}
      {renderContent()}
      <Md>{renderFilterMobile()}</Md>
      {renderFooter()}
    </LocomotiveScroll>
  )
}

export default Shop
